<script setup lang="ts">
import { computed, resolveComponent } from 'vue';
import { THEME } from '@pkg/config/misc/theme';
import type { Image } from '@sanity/types';
import { cn } from '@pkg/ui/lib/utils';
import type { ModularBlock } from '@pkg/sanity-utils/types/modularContentBlocks';
import type { VideoType } from '@pkg/sanity-utils/constants/video';
import VideoBackground from '@pkg/ui/components/base/VideoBackground.vue';
import ModularBlocksRenderer from '@pkg/modular-blocks/frontend/ModularBlocksRenderer.vue';
import { useResolvedSectionTheme } from '@pkg/modular-blocks/frontend/useResolvedSectionTheme';

interface BackgroundImage {
  image?: Image;
  video: never;
}

interface BackgroundVideo {
  video?: {
    url?: string;
    webm?: string;
    ogg?: string;
    fallbackImage?: Image;
    controls: boolean;
    autoplay: boolean;
    videoType?: VideoType;
  };
  image: never;
}

const props = defineProps<(BackgroundImage | BackgroundVideo) & {
  theme: THEME;
  content?: Array<ModularBlock>;
  offsetHeader?: boolean;
}>();

const resolvedTheme = useResolvedSectionTheme(() => props.theme);

const image = computed(() => props.image || props.video?.fallbackImage);
const showOverlay = computed(() => (props.video || props.image) && (props.content?.length ?? 0) > 0);

/** Host app registers these (PartialSection, BaseImage). */
const PartialSection = resolveComponent('PartialSection');
const BaseImage = resolveComponent('BaseImage');
</script>

<template>
  <component
    :is="PartialSection"
    :theme="resolvedTheme"
    class="c-main-hero"
  >
    <component
      :is="BaseImage"
      v-if="image"
      :image="image"
      loading="eager"
      class="c-main-hero__image"
    />
    <VideoBackground
      v-if="props.video?.url"
      class="c-main-hero__video-background"
      :url="props.video.url"
      :webm="props.video.webm"
      :ogg="props.video.ogg"
      :video-type="props.video.videoType"
      :fetchpriority="props.video?.fallbackImage ? undefined : 'high'"
      :controls="props.video.controls"
      :autoplay="props.video.autoplay"
    />

    <div v-if="showOverlay" class="c-main-hero__overlay" />

    <div class="c-main-hero__wrapper wrapper">
      <div
        :class="cn('c-main-hero__content', {
          'c-main-hero__content--offset-header': offsetHeader,
        })"
      >
        <slot>
          <ModularBlocksRenderer :blocks="props.content || []" />
        </slot>
      </div>
    </div>
  </component>
</template>

<style scoped>
.c-main-hero {
  display: flex;
  min-height: 100svh;
  position: relative;

  @screen tablet-wide {
    min-height: 840px;
  }

  &__image {
    height: 100%;
    left: 0;
    object-fit: cover;
    position: absolute;
    top: 0;
    width: 100%;
  }

  &__video-background {
    margin-top: calc(-1 * var(--section-padding-top));
    margin-bottom: calc(-1 * var(--section-padding-bottom));
    object-fit: cover;
    height: 100%;
  }

  &__overlay {
    background: linear-gradient(rgba(0,0,0,.2) 40%, rgba(0,0,0,.5));
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }

  &__wrapper {
    flex: 1;
    display: flex;
  }

  &__content {
    position: relative;
    flex: 1;
    z-index: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 1rem;

    @screen tablet-wide {
      gap: 1.5rem;
    }

    &--offset-header {
      justify-content: flex-end;
    }
  }
}
</style>
